/**
 * 게시글 본문의 인라인 이미지와 API 이미지 슬롯 간의 변환을 담당하는 유틸리티
 */

export interface ExtractedSlots {
  update_message: string;
  image2_url?: string;
  image3_url?: string;
  image4_url?: string;
  image5_url?: string;
}

/**
 * 스토리 이미지의 blob: src만 data: URL로 치환 (서버 업로드 없음).
 * 2→3 전환 직후 에디터가 아직 붙어 있을 때 호출 — 이후 Step3·완료 버튼에서 한 번에 업로드.
 */
export const embedStoryImageBlobsAsDataUrls = async (html: string): Promise<string> => {
  if (!String(html).trim()) return html;

  const blobToDataUrl = async (blobUrl: string): Promise<string> => {
    const blob = await fetch(blobUrl).then((r) => r.blob());
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
      reader.readAsDataURL(blob);
    });
  };

  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const imgs = Array.from(
    tpl.content.querySelectorAll<HTMLImageElement>('figure[data-embed="story-image"] img[src]'),
  );
  for (const img of imgs) {
    const src = img.getAttribute('src')?.trim() ?? '';
    if (!src.startsWith('blob:')) continue;
    try {
      const dataUrl = await blobToDataUrl(src);
      if (dataUrl) img.setAttribute('src', dataUrl);
    } catch {
      /* blob 만료 등 — src 유지, 저장 단계에서 오류 가능 */
    }
  }
  return tpl.innerHTML;
};

/**
 * 본문에 남아 있는 blob:/data: 스토리 이미지 src를 업로드한 URL로 바꿉니다.
 * (2단계 에디터 언마운트 시 blob이 revoke 되기 전에 호출해 3단계 미리보기·최종 저장 오류를 막음)
 */
export const replaceInlineStoryImageSrcsInHtml = async (
  html: string,
  resolveSrc: (src: string) => Promise<string>,
): Promise<string> => {
  if (!String(html).trim()) return html;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const imgs = Array.from(
    tpl.content.querySelectorAll<HTMLImageElement>('figure[data-embed="story-image"] img[src]'),
  );
  for (const img of imgs) {
    const src = img.getAttribute('src')?.trim() ?? '';
    if (!src) continue;
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      const url = await resolveSrc(src);
      if (url) img.setAttribute('src', url);
    }
  }
  return tpl.innerHTML;
};

/**
 * HTML 본문에서 인라인 이미지(figure[data-embed="story-image"])를 추출하여
 * 슬롯 플레이스홀더([[IMG_SLOT_N]])로 치환하고 image2_url~image5_url 값을 반환합니다.
 *
 * resolveImageUrl 콜백을 제공하면 data/blob URL을 업로드 URL로 치환할 수 있습니다.
 */
export const extractImageSlots = async (
  html: string,
  resolveImageUrl?: (src: string, slotNum: number) => Promise<string | null | undefined>,
): Promise<ExtractedSlots> => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const figures = doc.querySelectorAll('figure[data-embed="story-image"]');

  const result: ExtractedSlots = {
    update_message: '',
  };

  const figureList = Array.from(figures);
  for (let index = 0; index < figureList.length; index += 1) {
    const fig = figureList[index];
    const slotNum = index + 2; // image2부터 시작
    if (slotNum > 5) break; // 최대 5개 슬롯 제한

    const img = fig.querySelector('img');
    if (img) {
      const src = (img.getAttribute('src') || '').trim();
      if (src) {
        const resolvedUrl = resolveImageUrl ? await resolveImageUrl(src, slotNum) : src;
        if (resolvedUrl) {
          (result as any)[`image${slotNum}_url`] = resolvedUrl;
        }
      }

      // 본문 내의 이미지는 플레이스홀더로 치환
      const placeholder = document.createTextNode(`[[IMG_SLOT_${slotNum}]]`);
      fig.parentNode?.replaceChild(placeholder, fig);
    }
  }

  result.update_message = doc.body.innerHTML;
  return result;
};

/**
 * 본문의 슬롯 플레이스홀더([[IMG_SLOT_N]])를 실제 이미지 태그로 복원합니다.
 * 에디터에서 수정할 때 사용하거나 뷰어에서 렌더링할 때 사용합니다.
 */
export const restoreImageSlots = (html: string, postData: any): string => {
  if (!html) return '';
  
  let restoredHtml = html;
  
  for (let i = 2; i <= 5; i++) {
    const placeholder = `[[IMG_SLOT_${i}]]`;
    const url = postData[`image${i}_url` as keyof typeof postData];
    
    if (restoredHtml.includes(placeholder)) {
      if (url) {
        // 에디터용 figure 구조로 복원
        const replacement = `<figure data-embed="story-image" contenteditable="false"><img src="${url}" data-embed="image" width="100%"></figure>`;
        restoredHtml = restoredHtml.replace(placeholder, replacement);
      } else {
        // 이미지가 없으면 플레이스홀더 제거
        restoredHtml = restoredHtml.replace(placeholder, '');
      }
    }
  }
  
  return restoredHtml;
};
